import { combineReducers, configureStore, createListenerMiddleware } from '@reduxjs/toolkit';

import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createTransform,
  persistReducer,
  persistStore,
  type PersistConfig,
} from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';

import { authSlice, signOut } from '@/features/auth/store/authSlice';

import { baseApi } from './baseApi';
import { uiSlice } from './uiSlice';

const logoutListener = createListenerMiddleware();
logoutListener.startListening({
  actionCreator: signOut,
  effect: (_action, api) => {
    api.dispatch(baseApi.util.resetApiState());
  },
});

// ---------------------------------------------------------------------------
// Root reducer — add feature slices below as the project grows
// ---------------------------------------------------------------------------

const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  auth: authSlice.reducer,
  ui: uiSlice.reducer,
});

// ---------------------------------------------------------------------------
// Redux Persist
// Whitelist is opt-in. 'auth' persists the user across page refresh, never the
// access token: it stays in memory and SessionRestoreGate gets a new one from
// the refresh cookie on load.
// Never whitelist RTK Query cache — server data must always be re-fetched.
// ---------------------------------------------------------------------------

type AuthState = ReturnType<typeof authSlice.reducer>;

export const omitAccessToken = createTransform<AuthState, AuthState>(
  (state) => ({ ...state, token: null }),
  (state) => ({ ...state, token: null }),
  { whitelist: ['auth'] },
);

const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
  key: 'root',
  version: 1,
  storage: storageSession,
  whitelist: ['auth'],
  transforms: [omitAccessToken],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .prepend(logoutListener.middleware)
      .concat(baseApi.middleware),
});

export const persistor = persistStore(store);

// Infer types from non-persisted rootReducer for clean selector types
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
